import { useRef, useState } from 'react';
import { FileText, Film, Image as ImageIcon, Loader2, Upload } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const IMAGE_UPLOAD = {
  accept: '.jpg,.jpeg,.png,.gif,.webp,image/jpeg,image/png,image/gif,image/webp',
  allowedMimeTypes: ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
  allowedExtensions: ['.jpg', '.jpeg', '.png', '.gif', '.webp'],
  maxSizeMB: 20,
  endpoint: 'upload',
  kind: 'image',
};

export const VIDEO_UPLOAD = {
  accept: '.mp4,.mov,.webm,.avi,.mkv,video/*',
  allowedMimeTypes: ['video/mp4', 'video/quicktime', 'video/webm', 'video/x-msvideo', 'video/x-matroska'],
  allowedExtensions: ['.mp4', '.mov', '.webm', '.avi', '.mkv'],
  maxSizeMB: 50,
  endpoint: 'upload-video',
  kind: 'video',
};

export const PDF_UPLOAD = {
  accept: '.pdf,application/pdf',
  allowedMimeTypes: ['application/pdf'],
  allowedExtensions: ['.pdf'],
  maxSizeMB: 20,
  endpoint: 'upload',
  kind: 'pdf',
};


const extensionOf = (name = '') => {
  const dot = name.lastIndexOf('.');
  return dot >= 0 ? name.slice(dot).toLowerCase() : '';
};

export const validateUploadFile = (file, { allowedMimeTypes = [], allowedExtensions = [], maxSizeMB = 20 } = {}) => {
  if (!file) return 'Choose a file to upload.';
  const extensionAllowed = allowedExtensions.includes(extensionOf(file.name));
  const mimeAllowed = file.type ? allowedMimeTypes.includes(file.type.toLowerCase()) : false;
  if (allowedExtensions.length && !extensionAllowed) {
    return `Unsupported file type. Allowed: ${allowedExtensions.join(', ')}`;
  }
  if (file.type && allowedMimeTypes.length && !mimeAllowed) {
    return `Unsupported file type. Allowed: ${allowedExtensions.join(', ')}`;
  }
  if (file.size > maxSizeMB * 1024 * 1024) return `File must be ${maxSizeMB}MB or smaller.`;
  return null;
};

const FileDropzone = ({
  label,
  description,
  accept,
  allowedMimeTypes,
  allowedExtensions,
  maxSizeMB = 20,
  endpoint = 'upload',
  kind = 'file',
  multiple = false,
  maxFiles,
  disabled = false,
  onUploaded,
  onFilesSelected,
  successMessage,
  className = '',
  compact = false,
  testId = 'file-dropzone',
}) => {
  const inputRef = useRef(null);
  const dragDepth = useRef(0);
  const [dragActive, setDragActive] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);

  const Icon = kind === 'image' ? ImageIcon : kind === 'video' ? Film : kind === 'pdf' ? FileText : Upload;
  const isDisabled = disabled || uploading;

  const processFiles = async (fileList) => {
    let files = Array.from(fileList || []);
    if (!multiple) files = files.slice(0, 1);
    if (maxFiles != null) files = files.slice(0, Math.max(0, maxFiles));
    if (!files.length) return;

    for (const file of files) {
      const error = validateUploadFile(file, { allowedMimeTypes, allowedExtensions, maxSizeMB });
      if (error) {
        toast.error(`${file.name}: ${error}`);
        return;
      }
    }

    setUploading(true);
    setProgress(0);
    try {
      if (onFilesSelected) {
        await onFilesSelected(files);
      } else {
        for (let index = 0; index < files.length; index += 1) {
          const file = files[index];
          const data = new FormData();
          data.append('file', file);
          const response = await axios.post(`${API}/${endpoint}`, data, {
            headers: { 'Content-Type': 'multipart/form-data' },
            onUploadProgress: (event) => {
              const fileProgress = event.total ? event.loaded / event.total : 0;
              setProgress(Math.round(((index + fileProgress) / files.length) * 100));
            },
          });
          await onUploaded?.(response.data, file);
        }
      }
      setProgress(100);
      if (successMessage) toast.success(successMessage);
    } catch (error) {
      toast.error(error.response?.data?.detail || error.message || 'Upload failed');
    } finally {
      setUploading(false);
      setProgress(0);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const openPicker = () => {
    if (!isDisabled) inputRef.current?.click();
  };

  const handleDragEnter = (event) => {
    event.preventDefault();
    event.stopPropagation();
    dragDepth.current += 1;
    if (!isDisabled && event.dataTransfer?.types?.includes('Files')) setDragActive(true);
  };

  const handleDragLeave = (event) => {
    event.preventDefault();
    event.stopPropagation();
    dragDepth.current = Math.max(0, dragDepth.current - 1);
    if (!dragDepth.current) setDragActive(false);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();
    dragDepth.current = 0;
    setDragActive(false);
    if (!isDisabled) processFiles(event.dataTransfer.files);
  };

  return (
    <div className={className}>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        multiple={multiple}
        disabled={isDisabled}
        onChange={(event) => processFiles(event.target.files)}
        className="hidden"
        data-testid={`${testId}-input`}
      />
      <div
        role="button"
        tabIndex={isDisabled ? -1 : 0}
        aria-disabled={isDisabled}
        aria-label={label || `Upload ${kind}`}
        onClick={openPicker}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            openPicker();
          }
        }}
        onDragEnter={handleDragEnter}
        onDragOver={(event) => { event.preventDefault(); event.stopPropagation(); }}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        data-testid={testId}
        className={`group cursor-pointer rounded-xl border border-dashed transition-all focus:outline-none focus:ring-2 focus:ring-violet-500/60 ${
          dragActive ? 'border-violet-400 bg-violet-500/15' : 'border-[#3F3F46] bg-[#1E1E1E] hover:border-violet-500/60 hover:bg-violet-500/5'
        } ${isDisabled ? 'cursor-not-allowed opacity-60' : ''} ${compact ? 'p-3' : 'px-5 py-7'}`}
      >
        <div className={`flex items-center justify-center ${compact ? 'gap-3' : 'flex-col gap-2 text-center'}`}>
          {uploading ? <Loader2 className="w-6 h-6 text-violet-400 animate-spin" /> : <Icon className="w-6 h-6 text-violet-400" />}
          <div>
            <p className="text-sm font-medium text-white">
              {uploading ? `Uploading${progress ? ` ${progress}%` : '...'}` : dragActive ? 'Drop file here' : (label || 'Choose a file or drag and drop')}
            </p>
            {!uploading && <p className="mt-0.5 text-xs text-[#71717A]">{description || `Click to browse or drag and drop · max ${maxSizeMB}MB`}</p>}
          </div>
        </div>
        {uploading && (
          <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-[#27272A]" aria-live="polite">
            <div className="h-full rounded-full bg-violet-500 transition-all" style={{ width: `${Math.max(progress, 5)}%` }} />
          </div>
        )}
      </div>
    </div>
  );
};

export default FileDropzone;

export { resolveMediaUrl } from '@/lib/media';
